// Phase T2.0A Prompt 2.2 — REGISTERED PLAYERS roster (v3).
//
// Layout: two logical columns — PLAYER | PARTNER — with each player on
// their own row/card. Order is TRUE REGISTRATION ORDER (using the per-
// player `player1_registered_at` / `player2_registered_at` timestamps
// introduced in migration 028). Consolidation preserves those timestamps
// so two independent registrations that later become a team keep their
// original places in the roster.
//
// Wording:
//   confirmed              →  Partner: <name>
//   looking_for_partner    →  Partner: Looking for a partner
//   not_registered         →  Partner: Waiting for partner
//
// Names are always shown "First Last" (never "Last, First").
// Both real player and confirmed partner names are canonical member links.
//
// Prompt 2.2: `paidProfileIds` (Set) marks players with a positive-only
// "✓ Paid" indicator on the PLAYER column ONLY. Never repeated on the
// Partner column even though the same person appears there as their
// teammate's partner (see spec §8 & §10.L). Unpaid players show no
// indicator publicly.

import React from 'react';
import { Check } from '@phosphor-icons/react';
import MemberLink from '../MemberLink';
import { playerCountLabel } from '../../lib/capacity';

export default function RegistrationRoster({
  entries = [],
  title = 'Registered Players',
  maxEntries = null,
  eventType = 'doubles',
  paidProfileIds = null,   // Set<string> | null (null = no paid data known)
}) {
  const paidSet = paidProfileIds instanceof Set
    ? paidProfileIds
    : new Set(paidProfileIds || []);

  // Explode each doubles entry into per-player rows. Each row carries the
  // player's OWN individual registration timestamp so we can sort by true
  // order-of-registration across BOTH slots of any entry.
  //
  // Feb 2026 corrected doubles semantics: a slot with `<slot>_registered_at
  // IS NULL` means that player was merely NAMED (not personally
  // registered). We render such slots as "pending registration" on the
  // registered player's Partner column, and we do NOT emit a separate
  // roster row for the named-only player (they haven't registered).
  const rows = [];
  for (const e of entries) {
    const p1Registered = !!e.player1_registered_at;
    const p2Registered = !!e.player2_registered_at;
    const partnerNamedOnly = (
      e.partner_status === 'confirmed' &&
      e.player2_profile_id && !p2Registered
    );

    // player1 row — always emit if their timestamp is set.
    if (p1Registered) {
      rows.push({
        profileId: e.player1_profile_id,
        name: e.player1_name,
        registeredAt: e.player1_registered_at || e.created_at || '',
        partnerProfileId: (e.partner_status === 'confirmed' && p2Registered)
          ? e.player2_profile_id : null,
        partnerName: (e.partner_status === 'confirmed' && p2Registered)
          ? e.player2_name : null,
        partnerStatus: partnerNamedOnly
          ? 'named_pending'
          : e.partner_status,
        namedPartnerProfileId: partnerNamedOnly ? e.player2_profile_id : null,
        namedPartnerName: partnerNamedOnly ? e.player2_name : null,
      });
    }
    // player2 row — only when they've personally registered.
    if (e.partner_status === 'confirmed' && e.player2_profile_id && p2Registered) {
      rows.push({
        profileId: e.player2_profile_id,
        name: e.player2_name,
        registeredAt: e.player2_registered_at || e.created_at || '',
        partnerProfileId: e.player1_profile_id,
        partnerName: e.player1_name,
        partnerStatus: 'confirmed',
      });
    }
  }

  // Ascending by timestamp — earliest registrants first. Ties broken by
  // name so the ordering is stable.
  rows.sort((a, b) => {
    const ta = String(a.registeredAt || '');
    const tb = String(b.registeredAt || '');
    if (ta !== tb) return ta < tb ? -1 : 1;
    return (a.name || '').localeCompare(b.name || '');
  });

  const countLabel = playerCountLabel(entries, eventType, maxEntries);

  return (
    <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5" data-testid="registration-roster">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">
          {title} {rows.length > 0 && <span className="text-[#9aa39d]">· {rows.length}</span>}
        </div>
        <div className="text-[11.5px] text-[#6a766e]" data-testid="roster-player-count">
          {countLabel}
        </div>
      </div>

      {/* Column header row — only visible from sm+ where the two-column
          grid engages. Mobile stacks each row vertically instead. */}
      <div
        className="hidden sm:grid grid-cols-[1fr_1fr] gap-4 pb-2 mb-2 border-b border-[#f3f1ec] text-[10px] uppercase tracking-wider text-[#9aa39d]"
        aria-hidden
      >
        <div>Player</div>
        <div>Partner</div>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-[#6a766e]" data-testid="roster-empty">
          Nobody has registered yet — be the first!
        </div>
      ) : (
        <ul className="space-y-2" data-testid="roster-list">
          {rows.map((r, i) => (
            <li
              key={`${r.profileId}-${i}`}
              data-testid={`roster-row-${r.profileId}`}
              className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-1 sm:gap-4 items-center bg-[#fbfaf8] border border-[#f3f1ec] rounded-xl px-3 py-2.5"
            >
              <div className="text-[15px] text-[#013523] font-semibold truncate flex items-center gap-2">
                <MemberLink profileId={r.profileId} name={r.name} />
                {paidSet.has(r.profileId) && (
                  <span
                    data-testid={`roster-paid-${r.profileId}`}
                    title="Paid"
                    className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5"
                  >
                    <Check size={11} weight="bold" /> Paid
                  </span>
                )}
              </div>
              <PartnerCell row={r} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PartnerCell({ row }) {
  // Confirmed AND partner has personally registered → clickable name.
  if (row.partnerStatus === 'confirmed' && row.partnerProfileId) {
    return (
      <div className="text-[14px] text-[#2a332c] truncate">
        <span className="text-[#6a766e] text-[11px] uppercase tracking-wider mr-1 sm:hidden">
          Partner:
        </span>
        <MemberLink profileId={row.partnerProfileId} name={row.partnerName} />
      </div>
    );
  }
  // Named but partner has NOT personally registered yet — show their
  // name AND a "Pending Registration" chip so the roster is honest.
  if (row.partnerStatus === 'named_pending' && row.namedPartnerProfileId) {
    return (
      <div className="text-[14px] text-[#2a332c] truncate flex items-center gap-2"
           data-testid="roster-named-pending">
        <span className="text-[#6a766e] text-[11px] uppercase tracking-wider mr-1 not-italic sm:hidden">
          Partner:
        </span>
        <MemberLink profileId={row.namedPartnerProfileId} name={row.namedPartnerName} />
        <span
          title="Named as partner — has not personally registered yet"
          className="shrink-0 inline-flex items-center text-[10px] uppercase tracking-wider bg-[#f5eed8] text-[#7c5b1a] border border-[#c9a93a]/40 rounded-full px-1.5 py-0.5"
        >
          Pending Registration
        </span>
      </div>
    );
  }
  // Looking for a Partner → italic, not clickable.
  if (row.partnerStatus === 'looking_for_partner') {
    return (
      <div className="text-[14px] italic text-[#8a6d1a]" data-testid="roster-lfp">
        <span className="text-[#6a766e] text-[11px] uppercase tracking-wider mr-1 not-italic sm:hidden">
          Partner:
        </span>
        Looking for a partner
      </div>
    );
  }
  // Waiting for Partner to Register → italic, not clickable.
  return (
    <div className="text-[14px] italic text-[#9aa39d]" data-testid="roster-pending">
      <span className="text-[#6a766e] text-[11px] uppercase tracking-wider mr-1 not-italic sm:hidden">
        Partner:
      </span>
      Waiting for partner
    </div>
  );
}
